/**
 * ProjetsPage — G2 : l'écran des projets (vague 4b, mig 245).
 *
 * Le geste raconté par la mairie : la secrétaire lance « Élections » depuis un
 * modèle, donne LA date pivot (le scrutin), les paliers se pré-remplissent,
 * elle ajuste à la main — et ce sont SES dates qui commandent (rappels,
 * retard, progression). Décision D3.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiException, type ProjetDetail, type WorkflowDefinition } from '@/lib/api';
import { Card, Button, Badge, Alert, Field } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { WorkflowEnvoisSection } from '@/components/WorkflowEnvoisSection';
import {
  FolderKanban, Plus, ArrowLeft, Loader2, AlertCircle, Check, Trash2,
  CalendarClock, Paperclip, Info,
} from 'lucide-react';
import { Retroplanning } from '@/components/projets/Retroplanning';

const CHAMP = 'h-10 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent';

const SECTEURS: Array<{ v: string; l: string }> = [
  { v: '', l: '— Aucun secteur —' },
  { v: 'service_technique', l: 'Service technique' },
  { v: 'scolaire_cantine', l: 'Scolaire / cantine' },
  { v: 'petite_enfance', l: 'Petite enfance' },
  { v: 'urbanisme', l: 'Urbanisme' },
  { v: 'associations', l: 'Associations' },
  { v: 'grc', l: 'GRC' },
  { v: 'rh', l: 'RH' },
  { v: 'comptable', l: 'Comptabilité' },
];

function dateFr(d: string | null): string {
  if (!d) return '—';
  return new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', dateStyle: 'medium' })
    .format(new Date(`${d}T12:00:00Z`));
}

export function ProjetsPage() {
  const [projets, setProjets] = useState<Awaited<ReturnType<typeof api.projets.lister>>['projets']>([]);
  const [definitions, setDefinitions] = useState<WorkflowDefinition[]>([]);
  const [ouvert, setOuvert] = useState<ProjetDetail | null>(null);
  const [creation, setCreation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const { confirm, dialogElement } = useConfirmDialog();

  // Formulaire de lancement
  const [fIntitule, setFIntitule] = useState('');
  const [fCircuit, setFCircuit] = useState('');
  const [fSecteur, setFSecteur] = useState('');
  const [fPivot, setFPivot] = useState('');
  const [fPaliers, setFPaliers] = useState<Array<{ etatId: string; libelle: string; date: string }>>([]);
  const [fLieu, setFLieu] = useState('');
  const [fOccDebut, setFOccDebut] = useState('');
  const [fOccFin, setFOccFin] = useState('');
  const [lieux, setLieux] = useState<Array<{ id: string; nom: string }>>([]);
  // Mode EXPRESS (demande Henrico 26/07) : à partir de rien, paliers libres.
  const [express, setExpress] = useState(false);
  const [xPaliers, setXPaliers] = useState<Array<{ libelle: string; date: string }>>([
    { libelle: '', date: '' },
  ]);

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [p, d] = await Promise.all([
        api.projets.lister(),
        api.workflows.definitions().catch(() => ({ definitions: [] as WorkflowDefinition[] })),
      ]);
      setProjets(p.projets);
      setDefinitions(d.definitions.filter((x) => x.actif));
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Chargement impossible');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void charger(); }, [charger]);

  // Référentiel lieux (mig 203) — les bases existantes servent les projets.
  useEffect(() => {
    api.admin.lieux.list()
      .then((r) => setLieux(r.lieux.map((l) => ({ id: l.id, nom: l.nom }))))
      .catch(() => setLieux([]));
  }, []);

  async function action(fn: () => Promise<unknown>, ok?: string) {
    setError(null); setMsg(null);
    try {
      const res = await fn();
      if (ok) setMsg(ok);
      await charger();
      return res;
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Action impossible');
      return null;
    }
  }

  async function preRemplir() {
    if (!fCircuit || !fPivot) return;
    try {
      const r = await api.projets.preRemplissage(fCircuit, fPivot);
      setFPaliers(r.paliers.map((p) => ({
        etatId: p.etatId, libelle: p.libelle, date: p.dateProposee ?? '',
      })));
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Pré-remplissage impossible');
    }
  }

  async function lancer() {
    const corps = {
      intitule: fIntitule.trim(),
      secteur: fSecteur || null,
      lieuId: fLieu || null,
      occupation: fLieu && fOccDebut && fOccFin
        ? { dateDebut: fOccDebut, dateFin: fOccFin } : null,
    };
    const res = await action(() => express
      ? api.projets.express({
        ...corps,
        paliers: xPaliers
          .filter((p) => p.libelle.trim())
          .map((p) => ({ libelle: p.libelle.trim(), dateButoir: p.date || null })),
      })
      : api.projets.creer({
        ...corps,
        definitionId: fCircuit,
        datePivot: fPivot || null,
        echeances: fPaliers
          .filter((p) => p.date)
          .map((p) => ({ etatId: p.etatId, dateButoir: p.date })),
      }), 'Projet lancé — les rappels suivront vos dates.');
    if (res) {
      setCreation(false);
      setFIntitule(''); setFCircuit(''); setFSecteur(''); setFPivot(''); setFPaliers([]);
      setFLieu(''); setFOccDebut(''); setFOccFin(''); setXPaliers([{ libelle: '', date: '' }]);
      setOuvert(res as ProjetDetail);
    }
  }

  if (loading && !projets.length && !ouvert) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-ink-muted" aria-hidden="true" />
      </div>
    );
  }

  // ── Détail d'un projet ─────────────────────────────────────────────────────
  if (ouvert) {
    const pct = ouvert.progression.total
      ? Math.round((ouvert.progression.faits / ouvert.progression.total) * 100) : 0;
    return (
      <div className="space-y-3">
        {dialogElement}
        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
        {msg && <Alert variant="success" icon={Check}>{msg}</Alert>}

        <button type="button" onClick={() => { setOuvert(null); setMsg(null); setError(null); }}
          className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Tous les projets
        </button>

        <Card variant="glass" padded>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-ink">{ouvert.intitule}</h2>
              <p className="mt-0.5 text-xs text-ink-muted">
                Circuit : {ouvert.definitionLibelle}
                {ouvert.responsable ? ` · Responsable : ${ouvert.responsable}` : ''}
                {ouvert.datePivot ? ` · Date pivot : ${dateFr(ouvert.datePivot)}` : ''}
                {ouvert.lieuNom ? ` · Lieu : ${ouvert.lieuNom}` : ''}
                {ouvert.occupation
                  ? ` (occupé du ${dateFr(ouvert.occupation.dateDebut)} au ${dateFr(ouvert.occupation.dateFin)})`
                  : ''}
              </p>
            </div>
            <Badge variant={ouvert.instance.etat.estFinal ? 'success' : 'info'} size="sm">
              {ouvert.instance.etat.libelle}
            </Badge>
          </div>
          <div className="mt-3">
            <div className="flex items-center justify-between text-2xs text-ink-muted">
              <span>Progression</span>
              <span>{ouvert.progression.faits}/{ouvert.progression.total} paliers</span>
            </div>
            <div className="mt-1 h-2 overflow-hidden rounded-full bg-surface" role="progressbar"
              aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </Card>

        {/* Refonte UX, chantier 5 (maquette 8) — le rétroplanning visuel. */}
        <Card variant="glass" padded>
          <h3 className="mb-2 text-sm font-medium text-ink">Rétroplanning</h3>
          <Retroplanning projet={ouvert} />
        </Card>

        {/* Les paliers — la timeline dont les dates font foi */}
        <Card variant="glass" padded>
          <h3 className="mb-2 flex items-center gap-1.5 text-sm font-medium text-ink">
            <CalendarClock className="h-4 w-4" aria-hidden="true" /> Paliers
          </h3>
          <div className="space-y-1.5">
            {ouvert.paliers.map((p) => (
              <div key={p.etatId}
                className={`flex flex-wrap items-center gap-2 rounded-r-lg border px-2.5 py-1.5 ${
                  p.courant ? 'border-accent/60 bg-accent/5' : 'border-border/60'}`}>
                <span className="h-3 w-3 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: p.couleur ?? '#64748b' }} aria-hidden="true" />
                <span className={`text-sm ${p.atteint || p.courant ? 'text-ink' : 'text-ink-muted'}`}>
                  {p.libelle}
                </span>
                {p.courant && <Badge variant="info" size="sm">En cours</Badge>}
                {p.atteint && !p.courant && <Badge variant="success" size="sm">Fait</Badge>}
                {p.enRetard && <Badge variant="urgent" size="sm">En retard</Badge>}
                <div className="ml-auto flex items-center gap-1.5">
                  {p.note && <span className="text-2xs text-ink-muted" title={p.note}>📝</span>}
                  <input type="date" defaultValue={p.dateButoir ?? ''}
                    aria-label={`Date butoir de ${p.libelle}`}
                    onBlur={(e) => {
                      const v = e.target.value || null;
                      if (v !== p.dateButoir) {
                        void action(() => api.projets.setEcheance(ouvert.id, p.etatId, { dateButoir: v })
                          .then((r) => setOuvert(r)));
                      }
                    }}
                    className="h-8 rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink" />
                </div>
              </div>
            ))}
          </div>
          <p className="mt-2 flex items-start gap-1.5 text-2xs text-ink-muted">
            <Info className="mt-0.5 h-3 w-3 flex-shrink-0" aria-hidden="true" />
            Ces dates font foi : les rappels et le retard les suivent. Le rappel
            accompagne toujours le palier en cours.
          </p>
        </Card>

        {/* Les actions possibles depuis le palier courant */}
        {ouvert.instance.transitionsDisponibles.length > 0 && (
          <Card variant="glass" padded>
            <h3 className="mb-2 text-sm font-medium text-ink">Faire avancer</h3>
            <div className="flex flex-wrap gap-1.5">
              {ouvert.instance.transitionsDisponibles.map((t) => (
                t.pieceRequise ? (
                  <span key={t.id}>
                    <label htmlFor={`piece-${t.id}`}
                      className={`inline-flex cursor-pointer items-center gap-1.5 rounded-r-md border px-3 py-1.5 text-sm font-medium ${
                        t.autorisee ? 'border-border text-ink hover:border-accent/50' : 'cursor-not-allowed border-border/40 text-ink-muted opacity-50'}`}>
                      <Paperclip className="h-4 w-4" aria-hidden="true" /> {t.libelle}
                    </label>
                    <input id={`piece-${t.id}`} type="file" className="sr-only" disabled={!t.autorisee}
                      accept="application/pdf,image/jpeg,image/png,image/webp"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) {
                          void action(() => api.projets.transiter(ouvert.id, t.id, undefined, f)
                            .then((r) => setOuvert(r)), `« ${t.libelle} » — fait.`);
                        }
                        e.target.value = '';
                      }} />
                  </span>
                ) : (
                  <Button key={t.id} variant="secondary" size="sm" disabled={!t.autorisee}
                    title={t.autorisee ? undefined : 'Réservé à un autre rôle ou service'}
                    onClick={() => void action(() => api.projets.transiter(ouvert.id, t.id)
                      .then((r) => setOuvert(r)), `« ${t.libelle} » — fait.`)}>
                    {t.libelle}
                  </Button>
                )
              ))}
            </div>
          </Card>
        )}

        {/* L'historique */}
        <Card variant="glass" padded>
          <h3 className="mb-2 text-sm font-medium text-ink">Historique</h3>
          <div className="space-y-1">
            {ouvert.historique.map((h) => (
              <p key={h.id} className="text-xs text-ink-muted">
                <span className="text-ink">{dateFr(h.createdAt.slice(0, 10))}</span>
                {' — '}
                {h.deEtat ? `${h.deEtat} → ` : 'Lancement → '}
                <span className="text-ink">{h.versEtat}</span>
                {h.par ? ` · ${h.par}` : ''}
                {h.aPiece ? ' · 📎' : ''}
                {h.commentaire ? ` · « ${h.commentaire} »` : ''}
              </p>
            ))}
          </div>
        </Card>

        {/* Vague 4e — les envois du parcours (préparation / validation). */}
        <WorkflowEnvoisSection instanceId={ouvert.instance.id} />

        <Button variant="ghost" size="sm"
          onClick={() => {
            void (async () => {
              const ok = await confirm({
                title: `Supprimer « ${ouvert.intitule} » ?`,
                description: 'Le parcours, ses dates et ses rappels partent avec. Définitif.',
                confirmLabel: 'Supprimer',
              });
              if (ok) {
                const r = await action(() => api.projets.supprimer(ouvert.id), 'Projet supprimé.');
                if (r) setOuvert(null);
              }
            })();
          }}>
          <Trash2 className="h-4 w-4" aria-hidden="true" /> Supprimer le projet
        </Button>
      </div>
    );
  }

  // ── Liste + lancement ──────────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      {dialogElement}
      {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
      {msg && <Alert variant="success" icon={Check}>{msg}</Alert>}

      <div className="flex items-center justify-between gap-2">
        <h1 className="flex items-center gap-2 text-base font-semibold text-ink">
          <FolderKanban className="h-5 w-5" aria-hidden="true" /> Projets
        </h1>
        <div className="flex gap-1.5">
          <Button variant="primary" size="sm"
            onClick={() => { setExpress(false); setCreation(!creation || express); }}>
            <Plus className="h-4 w-4" aria-hidden="true" /> Depuis un circuit
          </Button>
          <Button variant="secondary" size="sm"
            onClick={() => { setExpress(true); setCreation(!creation || !express); }}>
            <Plus className="h-4 w-4" aria-hidden="true" /> Projet express
          </Button>
        </div>
      </div>

      {creation && (
        <Card variant="glass" padded className="space-y-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <Field label="Intitulé" htmlFor="pj-intitule">
              <input id="pj-intitule" value={fIntitule} onChange={(e) => setFIntitule(e.target.value)}
                placeholder="Ex : Élections municipales 2026" className={CHAMP} />
            </Field>
            {!express && (
            <Field label="Circuit (le modèle du parcours)" htmlFor="pj-circuit">
              <select id="pj-circuit" value={fCircuit}
                onChange={(e) => { setFCircuit(e.target.value); setFPaliers([]); }} className={CHAMP}>
                <option value="">—</option>
                {definitions.map((d) => <option key={d.id} value={d.id}>{d.libelle}</option>)}
              </select>
            </Field>
            )}
            <Field label="Secteur (cible des rappels)" htmlFor="pj-secteur">
              <select id="pj-secteur" value={fSecteur} onChange={(e) => setFSecteur(e.target.value)} className={CHAMP}>
                {SECTEURS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </Field>
            <Field label="Lieu concerné (référentiel)" htmlFor="pj-lieu">
              <select id="pj-lieu" value={fLieu} onChange={(e) => setFLieu(e.target.value)} className={CHAMP}>
                <option value="">— Aucun lieu —</option>
                {lieux.map((l) => <option key={l.id} value={l.id}>{l.nom}</option>)}
              </select>
            </Field>
            {fLieu && (
              <Field label="Occupation du lieu (bloque les réservations)" htmlFor="pj-occ-debut">
                <div className="flex items-center gap-1.5">
                  <input id="pj-occ-debut" type="date" value={fOccDebut}
                    onChange={(e) => setFOccDebut(e.target.value)}
                    aria-label="Début d'occupation" className={CHAMP} />
                  <span className="text-xs text-ink-muted">au</span>
                  <input type="date" value={fOccFin} min={fOccDebut || undefined}
                    onChange={(e) => setFOccFin(e.target.value)}
                    aria-label="Fin d'occupation" className={CHAMP} />
                </div>
              </Field>
            )}
            {!express && (
            <Field label="Date pivot (le scrutin, l'échéance…)" htmlFor="pj-pivot">
              <div className="flex gap-1.5">
                <input id="pj-pivot" type="date" value={fPivot}
                  onChange={(e) => setFPivot(e.target.value)} className={CHAMP} />
                <Button variant="secondary" size="sm" disabled={!fCircuit || !fPivot}
                  onClick={() => void preRemplir()}>
                  Pré-remplir
                </Button>
              </div>
            </Field>
            )}
          </div>

          {express && (
            <div>
              <p className="mb-1 text-xs text-ink-muted">
                Vos paliers, libres et datés : le petit projet de mairie
                (publicité, fête, n'importe quoi) sans passer par les circuits.
              </p>
              <div className="space-y-1.5">
                {xPaliers.map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={p.libelle} placeholder={`Palier ${i + 1} (ex : Maquette affiche)`}
                      onChange={(e) => {
                        const next = [...xPaliers];
                        next[i] = { ...p, libelle: e.target.value };
                        setXPaliers(next);
                      }}
                      aria-label={`Libellé du palier ${i + 1}`}
                      className={`${CHAMP} min-w-0 flex-1`} />
                    <input type="date" value={p.date}
                      onChange={(e) => {
                        const next = [...xPaliers];
                        next[i] = { ...p, date: e.target.value };
                        setXPaliers(next);
                      }}
                      aria-label={`Date du palier ${i + 1}`}
                      className="h-8 rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink" />
                    {xPaliers.length > 1 && (
                      <button type="button" aria-label={`Retirer le palier ${i + 1}`}
                        onClick={() => setXPaliers(xPaliers.filter((_, j) => j !== i))}
                        className="text-ink-muted hover:text-danger">✕</button>
                    )}
                  </div>
                ))}
              </div>
              <Button variant="ghost" size="sm" className="mt-1.5"
                onClick={() => setXPaliers([...xPaliers, { libelle: '', date: '' }])}>
                <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter un palier
              </Button>
            </div>
          )}

          {!express && definitions.length === 0 && (
            <Alert variant="warning" icon={Info}>
              Aucun circuit actif : ajoutez-en un depuis Paramètres → Circuits
              (dupliquez un modèle : Élections, AOT, Vérification ERP, Délibération…).
            </Alert>
          )}

          {fPaliers.length > 0 && (
            <div>
              <p className="mb-1 text-xs text-ink-muted">
                Dates proposées depuis la date pivot — ajustez-les : ce sont les
                vôtres qui feront foi.
              </p>
              <div className="space-y-1.5">
                {fPaliers.map((p, i) => (
                  <div key={p.etatId} className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm text-ink">{p.libelle}</span>
                    <input type="date" value={p.date} aria-label={`Date de ${p.libelle}`}
                      onChange={(e) => {
                        const next = [...fPaliers];
                        next[i] = { ...p, date: e.target.value };
                        setFPaliers(next);
                      }}
                      className="h-8 rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink" />
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button variant="primary" size="sm"
            disabled={fIntitule.trim().length < 2
              || (express ? !xPaliers.some((p) => p.libelle.trim()) : !fCircuit)}
            onClick={() => void lancer()}>
            Lancer le projet
          </Button>
        </Card>
      )}

      {projets.length === 0 && !creation ? (
        <Card variant="glass" padded className="text-center text-sm text-ink-muted">
          Aucun projet — lancez le premier depuis un circuit.
        </Card>
      ) : (
        <div className="space-y-2">
          {projets.map((p) => {
            const pct = p.progression.total
              ? Math.round((p.progression.faits / p.progression.total) * 100) : 0;
            return (
              <Card key={p.id} variant="glass" padded
                className="cursor-pointer transition-colors hover:border-accent/40"
                onClick={() => void api.projets.get(p.id).then(setOuvert).catch(() => setError('Ouverture impossible'))}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink">{p.intitule}</p>
                    <p className="text-xs text-ink-muted">
                      {p.etatCourant ? p.etatCourant.libelle : '—'}
                      {p.dateButoirCourante ? ` · butoir ${dateFr(p.dateButoirCourante)}` : ''}
                      {p.responsable ? ` · ${p.responsable}` : ''}
                    </p>
                    <div className="mt-1.5 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-surface">
                      <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1.5">
                    {p.enRetard && <Badge variant="urgent" size="sm">En retard</Badge>}
                    {p.etatCourant?.estFinal && <Badge variant="success" size="sm">Terminé</Badge>}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
